"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { Trophy, Calendar, BarChart3 } from "lucide-react";
import { SkeletonRow } from "@/components/common/Skeleton";

interface Inb {
    id: string;
    title: string;
    score: number;
    upvotes: number;
    downvotes: number;
    eventDate: string;
}

type ViewMode = "ranking" | "poll";

export default function RankingPage() {
    const [inbs, setInbs] = useState<Inb[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [viewMode, setViewMode] = useState<ViewMode>("ranking");

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
    const months = [
        "Styczeń",
        "Luty",
        "Marzec",
        "Kwiecień",
        "Maj",
        "Czerwiec",
        "Lipiec",
        "Sierpień",
        "Wrzesień",
        "Październik",
        "Listopad",
        "Grudzień",
    ];

    const fetchRanking = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                sort: "score",
                limit: "50",
            });
            if (year) params.set("year", year);
            if (month) params.set("month", month);

            const response = await fetch(`/api/inby?${params}`);
            const data = await response.json();
            setInbs(data.inbs || []);
        } catch (error) {
            console.error("Error fetching ranking:", error);
        } finally {
            setIsLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        fetchRanking();
    }, [fetchRanking]);

    const maxScore = Math.max(...inbs.map((i) => Math.abs(i.score)), 1);

    return (
        <div className="container mx-auto px-4 py-8 animate-fadeIn">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Trophy className="text-amber-500" />
                        Ranking inb
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Najpopularniejsze afery według głosów
                    </p>
                </div>

                {/* View mode toggle */}
                <div className="flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    <button
                        onClick={() => setViewMode("ranking")}
                        className={`px-3 sm:px-4 py-2 rounded-md transition-colors flex items-center gap-1 sm:gap-2 ${viewMode === "ranking"
                            ? "bg-[#6b90fc] text-white"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                            }`}
                    >
                        <Trophy size={18} />
                        <span className="hidden sm:inline">Ranking</span>
                    </button>
                    <button
                        onClick={() => setViewMode("poll")}
                        className={`px-3 sm:px-4 py-2 rounded-md transition-colors flex items-center gap-1 sm:gap-2 ${viewMode === "poll"
                            ? "bg-[#6b90fc] text-white"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                            }`}
                    >
                        <BarChart3 size={18} />
                        <span className="hidden sm:inline">Sondaż</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-8 p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                            <Calendar size={14} />
                            Rok
                        </label>
                        <select
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            className="input"
                        >
                            <option value="">Wszystkie</option>
                            {years.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <Calendar size={14} />
                            Miesiąc
                        </label>
                        <select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="input"
                            disabled={!year}
                        >
                            <option value="">Wszystkie</option>
                            {months.map((m, i) => (
                                <option key={i} value={i + 1}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Results */}
            {isLoading ? (
                <div className="space-y-3">
                    {[...Array(8)].map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : inbs.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    Brak inb w wybranym okresie
                </div>
            ) : viewMode === "ranking" ? (
                /* Ranking view */
                <div className="space-y-3">
                    {inbs.map((inb, index) => (
                        <Link
                            key={inb.id}
                            href={`/inby/${inb.id}`}
                            className="card flex items-center gap-4 hover:border-red-500/50 transition-colors"
                        >
                            <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${index === 0
                                    ? "bg-amber-500 text-black"
                                    : index === 1
                                        ? "bg-gray-300 text-black"
                                        : index === 2
                                            ? "bg-amber-700 text-white"
                                            : "bg-gray-100 text-gray-500"
                                    }`}
                            >
                                {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold truncate text-gray-900 dark:text-gray-100">{inb.title}</h3>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {inb.upvotes}↑ {inb.downvotes}↓
                                </p>
                            </div>
                            <div
                                className={`text-xl font-bold ${inb.score > 0
                                    ? "text-green-500"
                                    : inb.score < 0
                                        ? "text-red-500"
                                        : "text-gray-400"
                                    }`}
                            >
                                {inb.score > 0 ? `+${inb.score}` : inb.score}
                            </div>
                        </Link>
                    ))}
                </div>
            ) : (
                /* Poll/Bar view */
                <div className="space-y-4">
                    {inbs.map((inb, index) => {
                        const percentage = Math.abs(inb.score) / maxScore * 100;
                        return (
                            <Link key={inb.id} href={`/inby/${inb.id}`} className="block">
                                <div className="card p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium flex items-center gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">#{index + 1}</span>
                                            <span className="text-gray-900 dark:text-gray-100">{inb.title}</span>
                                        </span>
                                        <span
                                            className={`font-bold ${inb.score > 0
                                                ? "text-green-500"
                                                : inb.score < 0
                                                    ? "text-red-500"
                                                    : "text-gray-400"
                                                }`}
                                        >
                                            {inb.score > 0 ? `+${inb.score}` : inb.score}
                                        </span>
                                    </div>
                                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`poll-bar ${inb.score >= 0
                                                ? "bg-gradient-to-r from-green-500 to-emerald-600"
                                                : "bg-gradient-to-r from-red-500 to-rose-600"
                                                }`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
